/**
 * Catalog Manager
 * Gestiona el cambio entre catálogo principal (main) y catálogo de electrónica (electronics)
 */

class CatalogManager {
  constructor() {
    this.storageKey = 'catalog';
    this.defaultCatalog = 'main';
    this.init();
  }

  /**
   * Inicializa el gestor de catálogos
   */
  init() {
    // Ejecutar inmediatamente sin esperar
    this.initCatalog();

    // También ejecutar cuando el DOM esté listo por si acaso
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.initCatalog();
      });
    }

    // Escuchar cambios en la navegación del navegador
    window.addEventListener('popstate', () => {
      this.initCatalog();
    });
  }

  /**
   * Inicializa el catálogo leyendo query params y localStorage
   */
  initCatalog() {
    // Leer query parameter
    const urlParams = new URLSearchParams(window.location.search);
    const departmentParam = urlParams.get('department');

    let catalogValue = this.defaultCatalog;

    // Si hay query parameter, actualizar localStorage
    if (departmentParam === 'electronics') {
      catalogValue = 'electronics';
      localStorage.setItem(this.storageKey, catalogValue);
    } else if (departmentParam === 'main') {
      catalogValue = 'main';
      localStorage.setItem(this.storageKey, catalogValue);
    } else {
      // Leer de localStorage (persistencia)
      const storedCatalog = localStorage.getItem(this.storageKey);
      if (storedCatalog === 'electronics' || storedCatalog === 'main') {
        catalogValue = storedCatalog;
      }
    }

    // Aplicar inmediatamente en todas las páginas
    this.applyCatalogMode(catalogValue, true);
  }

  /**
   * Verifica si estamos en el homepage.
   * Considera: Liquid __themePageIsIndex, path / o /index, o ruta solo locale (p. ej. /en, /es).
   */
  isHomepage() {
    const path = window.location.pathname;
    const pathNorm = path.replace(/^\/|\/$/g, '');
    const segments = pathNorm ? pathNorm.split('/') : [];
    const isLocaleRoot = segments.length === 1 && /^[a-z]{2}(-[a-z]{2})?$/i.test(segments[0]);
    const pathIsHome = path === '/' || path === '/index' || path.endsWith('/index') || isLocaleRoot;
    if (typeof window.__themePageIsIndex === 'boolean') {
      return window.__themePageIsIndex || pathIsHome;
    }
    return pathIsHome;
  }

  /**
   * Aplica el modo de catálogo en todas las páginas
   * @param {string} catalog - 'main' o 'electronics'
   * @param {boolean} force - Si es true, aplica incluso fuera del homepage
   */
  applyCatalogMode(catalog, force = false) {
    // Aplicar en todas las páginas si se fuerza, o solo en homepage si no
    if (!force && !this.isHomepage()) {
      return;
    }

    // Remover estilo inline del head que se agregó antes (si existe)
    const inlineStyle = document.getElementById('catalog-inline-style');
    if (inlineStyle) {
      inlineStyle.remove();
    }

    // Función para aplicar cambios
    const applyChanges = () => {
      // Aplicar clase al body para cambiar headers y esquemas de color
      // Esto funciona en todas las páginas gracias a los estilos CSS
      if (catalog === 'electronics') {
        document.body.classList.add('catalog-electronics');
        document.documentElement.classList.add('catalog-electronics');
      } else {
        document.body.classList.remove('catalog-electronics');
        document.documentElement.classList.remove('catalog-electronics');
      }

      // Solo manejar secciones si estamos en el homepage
      if (this.isHomepage()) {
        const mainContent = document.getElementById('MainContent');
        if (!mainContent) {
          // Si MainContent no existe aún, intentar de nuevo en el siguiente frame
          requestAnimationFrame(applyChanges);
          return;
        }

        // Obtener todas las secciones dentro del main content
        const allSections = mainContent.querySelectorAll('.shopify-section');

        // Promueve las imagenes con data-src/data-srcset (nunca descargadas) a
        // src/srcset reales -- solo se llama sobre secciones que si se van a
        // mostrar. Ver snippets/lazy-catalog-image.liquid.
        const promoteLazyImages = (section) => {
          section.querySelectorAll('img[data-src], img[data-srcset]').forEach((img) => {
            if (img.dataset.srcset) {
              img.srcset = img.dataset.srcset;
              img.removeAttribute('data-srcset');
            }
            if (img.dataset.src) {
              img.src = img.dataset.src;
              img.removeAttribute('data-src');
            }
          });
        };

        // Modo electrónica: ocultar secciones que NO tienen la clase shopify-section-group-electronics-group
        if (catalog === 'electronics') {
          allSections.forEach((section) => {
            if (section.classList.contains('shopify-section-group-electronics-group')) {
              // Mostrar secciones de electrónica
              section.style.display = '';
              section.classList.remove('catalog-hidden');
              promoteLazyImages(section);
            } else {
              // Ocultar secciones principales
              section.style.display = 'none';
              section.classList.add('catalog-hidden');
            }
          });
        } else {
          // Modo principal (main): ocultar secciones que tienen la clase shopify-section-group-electronics-group
          allSections.forEach((section) => {
            if (section.classList.contains('shopify-section-group-electronics-group')) {
              // Ocultar secciones de electrónica
              section.style.display = 'none';
              section.classList.add('catalog-hidden');
            } else {
              // Mostrar secciones principales
              section.style.display = '';
              section.classList.remove('catalog-hidden');
              promoteLazyImages(section);
            }
          });
        }
      }
    };

    // Intentar aplicar inmediatamente
    applyChanges();

    // Sincronizar catalog_type al carrito para que theme app extensions y checkout lo usen
    this.syncCatalogTypeToCart(catalog);
  }

  /**
   * Escribe el tipo de catálogo en cart.attributes.catalog_type para uso en checkout y theme app extensions.
   * No sobrescribe el resto de atributos del carrito.
   * @param {string} catalog - 'main' o 'electronics'
   */
  async syncCatalogTypeToCart(catalog) {
    try {
      const cartRes = await fetch('/cart.js');
      if (!cartRes.ok) return;
      const cart = await cartRes.json();
      const attrs = { ...(cart.attributes || {}), catalog_type: String(catalog) };
      if (attrs.catalog_type === (cart.attributes || {}).catalog_type) return; // ya está actualizado
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: attrs }),
      });
    } catch (e) {
      // Silenciar: no bloquear la UI si el carrito falla
    }
  }

  /**
   * Obtiene el catálogo actual
   * @returns {string} - 'main' o 'electronics'
   */
  getCurrentCatalog() {
    const stored = localStorage.getItem(this.storageKey);
    return stored === 'electronics' || stored === 'main' ? stored : this.defaultCatalog;
  }
}

// Crear instancia global
const catalogManager = new CatalogManager();

// Exponer funciones globales para uso desde otros scripts
window.applyCatalogMode = (catalog, force = false) => {
  catalogManager.applyCatalogMode(catalog, force);
};

window.getCurrentCatalog = () => {
  return catalogManager.getCurrentCatalog();
};
